'use client';

import { useState, useCallback, useEffect, useMemo, useRef, startTransition } from 'react';
import { motion } from 'framer-motion';
import { ThemedImage } from '@/components/ui/themed-image';
import {
  Receipt,
  ShoppingCart,
  MapPin,
  Clock,
  Phone,
  User,
  ChatText,
  Leaf,
  PhoneCall,
  Warning,
  Spinner,
  CreditCard,
  Gift,
  Coin,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { getLoyaltyBalance } from '@/lib/api/loyalty';
import { useAuthStore } from '@/stores/auth-store';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { useOrderAvailability } from '@/hooks/use-order-availability';
import { isRestaurantOpen } from '@/lib/business-hours';
import { safeGetItem, safeSetItem } from '@/lib/safe-storage';
import { 
  useCheckoutStore, 
  selectContact,
  selectDeliveryDetails,
  selectDeliveryTime,
  selectPreferences,
} from '@/stores/checkout-store';
import { useCartStore, selectCartItems, selectCartTotal, selectMinOrderEligibleTotal, type CartItem } from '@/stores/cart-store';
import { useMoneyRules, isFreeDeliveryPromoActive } from '@/lib/money-rules';
import { useAddressStore, selectDeliveryCost, selectMinOrderSum, selectAddressHasHydrated, selectIsInDeliveryZone, selectSelectedAddress } from '@/stores/address-store';
import { toast } from 'sonner';
import { UnavailableItemsDialog, type UnavailableItem } from '@/components/features/cart/unavailable-items-dialog';
import { useAddressBook, useAddressBookMutations } from '@/hooks/use-address-book';
import { isSameAddress, suggestLabel, MAX_SAVED_ADDRESSES } from '@/lib/api/addresses';
import { detailsToAddressInput, validateAddressFields } from '@/lib/saved-address';
import { ymOrderCreated, ymCheckoutStep, getYmUid, getVkClickId, getTmrLvid, tmrOrderCreated } from '@/lib/analytics';
import { captureEvent, getCurrentAttribution, getFirstTouchAttribution } from '@/lib/source-tracker';

// ========== TYPES ==========

interface OrderSummaryStepProps {
  onBack?: () => void;
}

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// Отказ бэка со статусом и полем — раньше всё схлопывалось в new Error(err.error),
// поэтому ни аналитика, ни подсветка поля не знали, что именно не понравилось серверу.
class OrderRequestError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly field: string | null;

  constructor(message: string, status: number, code: string | null, field: string | null) {
    super(message);
    this.name = 'OrderRequestError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

// Телефон валидируем на фронте так же мягко, как бэк (libphonenumber, регион RU):
// 11 цифр с 7/8 в начале либо 10 цифр национального формата (мобильные 9xx и
// городские 3xx/4xx/8xx — их бэк тоже принимает, блокировать нельзя). Иначе
// «Некорректный номер» прилетал тостом уже на последнем шаге, где поля телефона нет.
const isValidRuPhone = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) return digits.startsWith('7') || digits.startsWith('8');
  if (digits.length === 10) return /^[3-9]/.test(digits);
  return false;
};

// Ключ, под которым отдаём платёжные данные странице /pay/redirect.
// Та же строка продублирована в src/app/pay/redirect/page.tsx — импортировать её
// оттуда нельзя, иначе в бандл страницы уедет вся корзина.
const PAY_REDIRECT_STORAGE_PREFIX = 'payRedirect:';

// ========== ORDER ITEM CARD ==========

function OrderItemCard({ item, isDark }: { item: CartItem; isDark: boolean }) {
  const isPromoCodeItem = item.bonusType === 'promo';
  const isSpecial = item.isBonus || !!item.bonusType;
  // Промокод бывает платным («ролл по промокоду за 99 ₽») — тогда это обычная
  // оплачиваемая позиция, а не подарок. Проверяем только промо, чтобы не задеть
  // статусные роллы: у них в каталоге своя цена, но клиенту они достаются даром.
  const isPaidPromo = item.bonusType === 'promo' && Number(item.product.price) > 0;
  const isFreeItem = !isPaidPromo && (item.isBonus ||
    ['promo', 'silver', 'gold', 'platinum', 'first_order'].includes(item.bonusType || ''));
  
  // Получаем название промокода из localStorage
  const [promoCodeName, setPromoCodeName] = useState<string | null>(null);
  
  useEffect(() => {
    if (isPromoCodeItem) {
      const storedPromoCode = localStorage.getItem('appliedPromoCode');
      setPromoCodeName(storedPromoCode);
    }
  }, [isPromoCodeItem]);
  
  // Определяем цвет для разных типов бонусов
  const getBonusColor = () => {
    if (isPromoCodeItem) return 'var(--bonus-promo)'; // Фиолетовый для промокода
    if (item.bonusType === 'silver') return 'var(--bonus-silver)';
    if (item.bonusType === 'gold') return 'var(--bonus-gold)';
    if (item.bonusType === 'platinum') return 'var(--bonus-platinum)';
    return 'var(--pink-500)'; // Розовый по умолчанию для first_order
  };
  
  // Определяем подпись для бонуса
  const getBonusLabel = () => {
    if (isPromoCodeItem && promoCodeName) return `по промокоду "${promoCodeName}"`;
    if (item.bonusType === 'silver') return 'Silver бонус';
    if (item.bonusType === 'gold') return 'Gold бонус';
    if (item.bonusType === 'platinum') return 'Platinum бонус';
    return 'Подарок';
  };
  
  const bonusColor = getBonusColor();
  
  return (
    <div className={cn(
      'flex items-center gap-3 py-2',
      isDark ? 'border-[var(--border-default)]' : 'border-[var(--border-default)]'
    )}>
      <div className={cn(
        'relative w-12 h-12 rounded-card overflow-hidden flex-shrink-0',
        isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-[var(--surface-secondary)]'
      )}>
        {isPromoCodeItem ? (
          // Иконка подарка для промокода
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{
              background: isDark 
                ? 'var(--primary)' 
                : 'var(--primary-subtle)',
            }}
          >
            <Gift 
              weight="fill" 
              className="w-6 h-6" 
              style={{ color: isDark ? 'var(--fuchsia-200)' : 'var(--bonus-promo)' }}
            />
          </div>
        ) : (
          <>
            <ThemedImage
              src={item.product.image_url}
              alt={item.product.name}
              fill
              className="object-cover"
              sizes="48px"
            />
            {item.quantity > 1 && !isSpecial && (
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-pill bg-[var(--primary)] text-[var(--text-inverse)] text-[10px] font-bold flex items-center justify-center">
                {item.quantity}
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className={cn(
          'text-sm font-medium truncate',
          isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
        )}>
          {/* «Подарок» только для бесплатного промокода. У платного показываем
              название товара — человек за него платит и должен видеть, что берёт. */}
          {isPromoCodeItem && !isPaidPromo ? 'Подарок' : item.product.name}
        </h4>
        {isSpecial && (
          <span 
            className="text-xs font-medium"
            style={{ color: bonusColor }}
          >
            {getBonusLabel()}
          </span>
        )}
      </div>
      
      <span className={cn(
        'text-sm font-semibold font-mono',
        isFreeItem ? '' : (isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]')
      )}
      style={isFreeItem ? { color: bonusColor } : undefined}
      >
        {isFreeItem ? 'Бесплатно' : formatPrice(item.product.price * item.quantity)}
      </span>
    </div>
  );
}

// ========== INFO ROW ==========

function InfoRow({ 
  icon: Icon, 
  label, 
  value, 
  isDark 
}: { 
  icon: React.ElementType; 
  label: string; 
  value: string | React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon weight="regular" className={cn(
        'w-5 h-5 flex-shrink-0 mt-0.5',
        isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]'
      )} />
      <div className="flex-1">
        <span className={cn(
          'text-xs block',
          isDark ? 'text-[var(--text-secondary)]' : 'text-[var(--text-secondary)]'
        )}>
          {label}
        </span>
        <span className={cn(
          'text-sm',
          isDark ? 'text-[var(--text-primary)]' : 'text-[var(--text-primary)]'
        )}>
          {value}
        </span>
      </div>
    </div>
  );
}

// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function OrderSummaryStep(_props: OrderSummaryStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Order availability check
  const { checkAvailability } = useOrderAvailability();
  
  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unavailable, setUnavailable] = useState<UnavailableItem[] | null>(null);

  // Cart Store
  const cartItems = useCartStore(selectCartItems);
  const cartTotal = useCartStore(selectCartTotal);
  // Сумма для проверки минимального заказа — без бесплатных соусов (id 1-7).
  const minOrderEligible = useCartStore(selectMinOrderEligibleTotal);
  
  // Address Store
  const addressHasHydrated = useAddressStore(selectAddressHasHydrated);
  const deliveryCostRaw = useAddressStore(selectDeliveryCost);
  const minOrderSumRaw = useAddressStore(selectMinOrderSum);
  const isInDeliveryZone = useAddressStore(selectIsInDeliveryZone);
  const selectedAddress = useAddressStore(selectSelectedAddress);
  
  // Checkout Store
  const contact = useCheckoutStore(selectContact);
  const deliveryDetails = useCheckoutStore(selectDeliveryDetails);
  const deliveryTime = useCheckoutStore(selectDeliveryTime);
  const preferences = useCheckoutStore(selectPreferences);
  const loyaltyCoinsUsed = useCheckoutStore((state) => state.loyaltyCoinsUsed);
  const setLoyaltyCoins = useCheckoutStore((state) => state.setLoyaltyCoins);
  const setErrors = useCheckoutStore((state) => state.setErrors);
  const setShowValidationMessage = useCheckoutStore((state) => state.setShowValidationMessage);
  const setStep = useCheckoutStore((state) => state.setStep);
  
  // Auth
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Адресная книга — чтобы после заказа запомнить адрес и в следующий раз
  // не спрашивать подъезд, этаж и домофон заново.
  const { addresses: savedAddresses } = useAddressBook();
  const { create: createAddress, update: updateAddress } = useAddressBookMutations();

  /**
   * Кладёт адрес заказа в книгу (или освежает уже сохранённый).
   *
   * Вызывается ПОСЛЕ того, как заказ создан, и ничего не ждёт: книга — это
   * удобство, а заказ — деньги. Любой сбой здесь молча игнорируется и не имеет
   * права ни задержать переход к оплате, ни показать человеку ошибку.
   */
  const rememberDeliveryAddress = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) return;

    const details = useCheckoutStore.getState().deliveryDetails;
    if (!details.address.trim()) return;

    const existing = savedAddresses.find((saved) => isSameAddress(saved, details));

    const input = detailsToAddressInput(details, {
      label: existing?.label || suggestLabel(details.address, savedAddresses.length),
      type: existing?.type ?? (savedAddresses.length === 0 ? 'home' : 'other'),
      isDefault: existing ? existing.is_default === 1 : savedAddresses.length === 0,
    });
    // Комментарий курьеру человек писал руками в профиле — заказ его не трогает.
    if (existing) input.comment = existing.comment;

    try {
      if (existing) {
        const unchanged =
          existing.entrance === input.entrance &&
          existing.floor === input.floor &&
          existing.intercom === input.intercom &&
          (existing.has_intercom === 1) === input.has_intercom &&
          (existing.is_private_house === 1) === input.is_private_house;
        if (unchanged) return;
        await updateAddress.mutateAsync({ id: existing.id, input });
      } else if (savedAddresses.length < MAX_SAVED_ADDRESSES) {
        await createAddress.mutateAsync(input);
      }
    } catch {
      /* книга не сложилась — на заказ это не влияет */
    }
  }, [isAuthenticated, savedAddresses, createAddress, updateAddress]);

  // Loyalty balance
  const { data: loyaltyData } = useQuery({
    queryKey: ['loyalty-balance'],
    queryFn: getLoyaltyBalance,
    enabled: isAuthenticated,
    staleTime: 30000,
  });

  const coinBalance = loyaltyData?.balance ?? 0;
  const maxSpendPercent = loyaltyData?.max_spend_percent ?? 15;
  const minCartForSpend = loyaltyData?.min_cart_for_spend ?? 1500;

  // Мемоизированные вычисления
  const deliveryCost = useMemo(() => addressHasHydrated ? deliveryCostRaw : 0, [addressHasHydrated, deliveryCostRaw]);
  const minOrderSum = useMemo(() => addressHasHydrated ? minOrderSumRaw : 0, [addressHasHydrated, minOrderSumRaw]);
  // extras уже включены в cartTotal (добавлены в cart store через addItem),
  // поэтому НЕ прибавляем extrasTotal повторно
  const subtotal = useMemo(() => cartTotal, [cartTotal]);

  // Акция бесплатной доставки. Пороги приходят с сервера и правятся в админке,
  // поэтому берём их хуком: без подписки экран не перерисуется, когда правила
  // приедут, и покажет вчерашний порог рядом со свежей суммой.
  // Условие по фактической стоимости зоны (deliveryCost) и сумме товаров (cartTotal).
  // Итоговую бесплатность подтверждает бэк (orders.js) — здесь только отображение/payload.
  const moneyRules = useMoneyRules();
  const isFreeDeliveryPromo = useMemo(
    () => isFreeDeliveryPromoActive(deliveryCost, cartTotal, moneyRules),
    [deliveryCost, cartTotal, moneyRules]
  );
  const effectiveDeliveryCost = useMemo(
    () => (isFreeDeliveryPromo ? 0 : deliveryCost),
    [isFreeDeliveryPromo, deliveryCost]
  );

  // Loyalty: max coins allowed
  //
  // База расчёта — сумма товаров ВМЕСТЕ С ДОСТАВКОЙ. Так считает сервер
  // (orders.js: serverTotal = товары + доставка), и это решение владельца по
  // вопросу 3: «сервер прав, клиенты подтягиваются». Раньше здесь порог
  // сравнивался с subtotal (без доставки), а лимит считался уже с доставкой —
  // то есть заказ на 1400 ₽ + 265 ₽ доставки сервер к списанию допускал, а
  // витрина показывала «коины недоступны».
  //
  // Доставка берётся УЖЕ с учётом акции бесплатной доставки (effectiveDeliveryCost):
  // сервер обнуляет её до расчёта коинов, значит и здесь она должна быть нулём.
  const coinsBaseTotal = useMemo(
    () => subtotal + effectiveDeliveryCost,
    [subtotal, effectiveDeliveryCost]
  );
  const canSpendCoins = isAuthenticated && coinBalance > 0 && coinsBaseTotal >= minCartForSpend;
  const maxCoinsAllowed = useMemo(() => {
    if (!canSpendCoins) return 0;
    return Math.min(coinBalance, Math.floor(coinsBaseTotal * maxSpendPercent / 100));
  }, [canSpendCoins, coinBalance, coinsBaseTotal, maxSpendPercent]);

  // Clamp coins used
  const effectiveCoins = useMemo(() => Math.min(loyaltyCoinsUsed, maxCoinsAllowed), [loyaltyCoinsUsed, maxCoinsAllowed]);

  const total = useMemo(() => subtotal + effectiveDeliveryCost - effectiveCoins, [subtotal, effectiveDeliveryCost, effectiveCoins]);

  // Мемоизированное форматирование цен
  const formattedSubtotal = useMemo(() => formatPrice(subtotal), [subtotal]);
  const formattedDeliveryCost = useMemo(() => effectiveDeliveryCost > 0 ? formatPrice(effectiveDeliveryCost) : 'Бесплатно', [effectiveDeliveryCost]);
  const formattedTotal = useMemo(() => formatPrice(total), [total]);
  const formattedRemaining = useMemo(() => formatPrice(minOrderSum - minOrderEligible), [minOrderSum, minOrderEligible]);
  
  // Validate order
  const validateOrder = useCallback(() => {
    const newErrors: Record<string, boolean> = {};
    
    if (!contact.name) newErrors.name = true;
    if (!contact.phone || !isValidRuPhone(contact.phone)) newErrors.phone = true;
    if (!deliveryDetails.address) newErrors.address = true;
    
    // Critical: verify address is actually in delivery zone with valid delivery cost
    if (!selectedAddress || !isInDeliveryZone) {
      newErrors.address = true;
    }
    
    // Те же правила, что и на шаге «Данные доставки» (см. lib/saved-address.ts):
    // квартира, подъезд и этаж обязательны в многоквартирном доме, домофон —
    // только если он есть. Раньше здесь проверялась одна квартира, и человек
    // доходил до оплаты, а курьер — не до подъезда.
    const missingAddressFields = validateAddressFields(deliveryDetails);
    for (const field of missingAddressFields) {
      newErrors[field] = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setShowValidationMessage(true);

      // Гейт оформления раньше молчал: в воронке человек, упёршийся в валидацию,
      // был неотличим от закрывшего вкладку.
      let blockReason = 'contacts';
      if (newErrors.address && (!selectedAddress || !isInDeliveryZone)) {
        blockReason = 'out_of_zone';
        toast.error('Укажите адрес в зоне доставки');
      } else if (missingAddressFields.length > 0) {
        blockReason = 'apartment';
        toast.error(
          missingAddressFields.length === 1 && missingAddressFields[0] === 'apartment'
            ? 'Укажите номер квартиры'
            : 'Заполните данные квартиры'
        );
      } else if (newErrors.phone && contact.phone) {
        blockReason = 'phone_invalid';
        toast.error('Проверьте номер телефона');
      } else if (newErrors.name || newErrors.phone) {
        toast.error('Заполните контактные данные');
      }

      captureEvent('checkout_blocked', {
        reason: blockReason,
        cart_value: cartTotal,
        min_order_sum: minOrderSum,
        zone_id: selectedAddress?.deliveryZone?.id ?? null,
        point_id: selectedAddress?.deliveryPointId ?? null,
      });

      startTransition(() => {
        setStep('delivery-info');
      });
      return false;
    }

    // Check minimum order (без бесплатных соусов id 1-7)
    if (minOrderEligible < minOrderSum && minOrderSum > 0) {
      toast.error(`Минимальная сумма заказа: ${formatPrice(minOrderSum)}`);
      captureEvent('checkout_blocked', {
        reason: 'min_order',
        cart_value: cartTotal,
        min_order_sum: minOrderSum,
        zone_id: selectedAddress?.deliveryZone?.id ?? null,
        point_id: selectedAddress?.deliveryPointId ?? null,
      });
      startTransition(() => {
        setStep('cart');
      });
      return false;
    }

    return true;
  }, [contact, deliveryDetails, selectedAddress, isInDeliveryZone, minOrderEligible, minOrderSum, cartTotal, setErrors, setShowValidationMessage, setStep]);
  
  // Redirect via НАШУ промежуточную страницу /pay/redirect.
  //
  // Раньше делали form.submit() напрямую на paykeeper.ru — клиент мгновенно
  // улетал на чужой домен, мы не видели что там происходит. Текущая метрика
  // payment_abandoned=85% (!) показывает что 8 из 10 клиентов туда попадают
  // и НЕ платят. Чтобы понять причину — нужна точка контроля между нами и
  // PayKeeper. Эта страница:
  //   - захватывает событие payment_form_visited (гарантия что клиент дошёл)
  //   - делает auto-submit через 800мс
  //   - если auto-submit заблокирован (Safari/Brave иногда) → кнопка ручного клика
  //   - если за 8 секунд ничего — даёт телефон ресторана
  //
  // Телефон клиента и готовую ссылку на платёжку (в ней тоже есть client_phone)
  // НЕ кладём в query: URL страницы целиком уезжает в аналитику (events.page_url,
  // sessions.landing_url/exit_url) и в историю браузера. Передаём через
  // sessionStorage, в адресе остаётся только номер заказа, сумма и домен.
  const goToPayRedirect = (opts: {
    orderId: number;
    sum: number;
    domain: string;
    fallbackUrl?: string;
    phone: string;
  }) => {
    const clientPhone = opts.phone.replace(/\D/g, '');
    const params = new URLSearchParams({
      order: String(opts.orderId),
      sum: Number(opts.sum).toFixed(2),
      domain: opts.domain || 'fallback',
    });

    const storageKey = `${PAY_REDIRECT_STORAGE_PREFIX}${opts.orderId}`;
    safeSetItem(
      storageKey,
      JSON.stringify({ fallback: opts.fallbackUrl || null, phone: clientPhone }),
      'session'
    );

    // sessionStorage может быть недоступен (Safari «блокировать все cookie»,
    // Lockdown Mode). Терять из-за этого оплату нельзя — тогда передаём ссылку
    // прежним способом, через query.
    if (opts.fallbackUrl && safeGetItem(storageKey, 'session') === null) {
      params.set('fallback', opts.fallbackUrl);
    }

    window.location.href = `/pay/redirect?${params.toString()}`;
  };

  // Гард от двойного клика. isSubmitting — это setState, он не успевает примениться
  // до первого await (checkAvailability), поэтому два быстрых клика создавали два заказа.
  const inFlightRef = useRef(false);

  // Submit order
  const handleSubmit = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSubmitting(true);

    // Уходим на оплату — гард не снимаем, страница уже загружается.
    let redirecting = false;

    try {
      if (!validateOrder()) return;

      // Check business hours and technical break
      const available = await checkAvailability();
      if (!available) {
        captureEvent('checkout_blocked', {
          reason: deliveryTime.type === 'now' && !isRestaurantOpen() ? 'closed_now' : 'tech_break',
          cart_value: cartTotal,
          min_order_sum: minOrderSum,
          zone_id: selectedAddress?.deliveryZone?.id ?? null,
          point_id: selectedAddress?.deliveryPointId ?? null,
        });
        return;
      }

      // crypto.randomUUID есть не везде (старые WebView) — без fallback бросок
      // происходил вне try и кнопка навсегда залипала в «Оформляем заказ...».
      const clientRef =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Build order payload.
      // Состав берём ТОЛЬКО из корзины: extras (соусы/приборы) кладутся в неё через
      // addItem в extras-step, а сумма на экране считается по корзине. Отдельная
      // ветка по extras давала другой состав, чем видел клиент.
      const orderPayload = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.product.name,
          price: item.product.price,
          quantity: item.quantity,
          // ВАЖНО: отправляем ТОЛЬКО реальный sku из товара. Никакого fallback на id —
          // это ломает FrontPad (invalid_product_keys), он принимает артикул, а не id.
          // Если sku пуст — это аномалия данных, бэк примет решение (для бонусных пустой OK).
          sku: item.product.sku || '',
          isBonus: item.isBonus || false,
          bonusType: item.bonusType || null,
        })),
        delivery: {
          type: deliveryTime.type,
          date: deliveryTime.selectedDate || null,
          time: deliveryTime.selectedTime || null,
          address: deliveryDetails.address,
          apartment: deliveryDetails.isPrivateHouse ? '' : deliveryDetails.apartment,
          entrance: deliveryDetails.isPrivateHouse ? '' : deliveryDetails.entrance,
          floor: deliveryDetails.isPrivateHouse ? '' : deliveryDetails.floor,
          // «Нет домофона» — это пустое поле, а не прочерк: иначе курьер получит
          // код домофона «-» и будет его набирать.
          intercom:
            deliveryDetails.isPrivateHouse || !deliveryDetails.hasIntercom
              ? ''
              : deliveryDetails.intercom,
          cost: effectiveDeliveryCost,
          point: selectedAddress?.deliveryPointId
            ? { id: selectedAddress.deliveryPointId }
            : undefined,
          zone: selectedAddress?.deliveryZone
            ? { id: selectedAddress.deliveryZone.id }
            : undefined,
        },
        contact: {
          name: contact.name,
          phone: contact.phone.replace(/\D/g, ''),
          needCall: preferences.needCall,
        },
        preferences: {
          noGreens: preferences.noGreens,
          comment: deliveryDetails.comment,
        },
        total,
        loyalty_coins_used: effectiveCoins > 0 ? effectiveCoins : 0,
        source: 'web',
        ym_uid: getYmUid(),
        vk_click_id: getVkClickId(),
        tmr_lvid: getTmrLvid(),
        client_ref: clientRef,
        // ── Snapshot собственной атрибуции (last-touch + first-touch) ──
        // Бэк запишет в orders.attribution_* и в conversions.
        attribution: getCurrentAttribution(),
        first_touch: getFirstTouchAttribution(),
      };

      // ── Behavioral tracking: payment_started ──
      captureEvent('payment_started', {
        items_count: orderPayload.items.length,
        delivery_type: deliveryTime.type,
        has_loyalty: effectiveCoins > 0,
      }, { value: total, currency: 'RUB' });

      type OrderResponse = {
        order: { id: number; total: number };
        paykeeperDomain: string;
        paymentUrl?: string;
      };

      // Маркер сетевого обрыва: TypeError + типичные паттерны fetch failure.
      // Сюда НЕ попадают серверные ошибки (response.ok === false), только TCP/DNS/CORS-обрывы.
      const isNetworkError = (e: unknown): boolean =>
        e instanceof TypeError &&
        /load failed|failed to fetch|networkerror|network request failed|the network connection was lost/i.test(
          e.message
        );

      // Одна попытка POST. Возвращает данные, либо кидает.
      // Сюда не дойдут unavailableItems — их обрабатываем сразу здесь как штатный кейс.
      const attemptPost = async (): Promise<OrderResponse | 'unavailable-handled'> => {
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderPayload),
        });

        if (!response.ok) {
          const err = (await response.json().catch(() => ({ error: 'Ошибка сервера' }))) as {
            error?: string;
            code?: string;
            field?: string;
            unavailableItems?: UnavailableItem[];
          };

          // Бэк присылает unavailableItems, когда товары скрыты/удалены/недоступны.
          if (Array.isArray(err.unavailableItems) && err.unavailableItems.length > 0) {
            const cartApi = useCartStore.getState();
            err.unavailableItems.forEach((it) => {
              cartApi.removeItem(it.id);
              // removeItem снимает только платные строки. Тот же товар мог лежать
              // подарком/призом — его убираем своим экшеном, иначе недоступная
              // позиция осталась бы в корзине.
              const special = useCartStore
                .getState()
                .items.find((ci) => ci.id === it.id && (ci.isBonus || !!ci.bonusType));
              if (!special) return;
              if (special.bonusType === 'promo') cartApi.removePromoItem();
              else if (special.bonusType === 'roulette') cartApi.removeRouletteItem();
              else if (special.bonusType === 'silver' || special.bonusType === 'gold' || special.bonusType === 'platinum') {
                cartApi.removeStatusRolls();
              } else cartApi.removeBonusItems();
            });
            setUnavailable(err.unavailableItems);
            return 'unavailable-handled';
          }

          throw new OrderRequestError(
            err.error || `Ошибка ${response.status}`,
            response.status,
            err.code ?? null,
            err.field ?? null,
          );
        }

        return (await response.json()) as OrderResponse;
      };

      // Recovery: проверить, не создался ли заказ несмотря на network error.
      const tryRecover = async (): Promise<OrderResponse | null> => {
        try {
          const recoverRes = await fetch(
            `/api/orders/recover-pending?client_ref=${encodeURIComponent(clientRef)}`,
            { cache: 'no-store' }
          );
          if (recoverRes.ok) {
            return (await recoverRes.json()) as OrderResponse;
          }
        } catch (recoverErr) {
          console.error('Recovery attempt failed:', recoverErr);
        }
        return null;
      };

      let data: OrderResponse;
      try {
        const firstResult = await attemptPost();
        if (firstResult === 'unavailable-handled') return;
        data = firstResult;
      } catch (firstErr) {
        // Серверные ошибки (400/500 c JSON-телом) — пробрасываем как есть.
        if (!isNetworkError(firstErr)) throw firstErr;

        // ── Сетевой обрыв ──
        // Трекаем для метрики "сколько кликов "Оплатить" срывается на сети".
        captureEvent('payment_network_error', {
          phase: 'post_orders',
          message: firstErr instanceof Error ? firstErr.message : String(firstErr),
        });

        // Ждём 1.2s — заказ мог проскочить на бэк, но ответ не дошёл.
        // MySQL под нагрузкой может задержать запись на ~500ms.
        await new Promise((r) => setTimeout(r, 1200));

        // 1) Recovery: может заказ уже создался.
        const recovered = await tryRecover();
        if (recovered) {
          console.log('Order recovered after network error:', recovered.order.id);
          captureEvent('payment_recovered', { order_id: recovered.order.id, attempt: 'recover' });
          data = recovered;
        } else {
          // 2) Заказ НЕ создался — пробуем POST ещё раз (тот же client_ref защитит от дубля).
          try {
            const secondResult = await attemptPost();
            if (secondResult === 'unavailable-handled') return;
            captureEvent('payment_recovered', { order_id: secondResult.order.id, attempt: 'retry_post' });
            data = secondResult;
          } catch (secondErr) {
            // Финальный fallback: ещё одна попытка recovery (вдруг второй POST дошёл, а ответ нет).
            if (isNetworkError(secondErr)) {
              await new Promise((r) => setTimeout(r, 1200));
              const recovered2 = await tryRecover();
              if (recovered2) {
                captureEvent('payment_recovered', { order_id: recovered2.order.id, attempt: 'recover_after_retry' });
                data = recovered2;
              } else {
                captureEvent('payment_failed_final', {
                  phase: 'post_orders',
                  message: secondErr instanceof Error ? secondErr.message : String(secondErr),
                });
                toast.error(
                  'Не удалось связаться с сервером. Проверьте интернет или позвоните +7 (863) 333-50-50 — мы оформим заказ по телефону.',
                  { duration: 10000 }
                );
                return;
              }
            } else {
              throw secondErr;
            }
          }
        }
      }

      // ── Яндекс.Метрика: заказ создан ──
      ymOrderCreated(data.order.id, data.order.total);
      // ── Top.Mail.Ru: заказ создан ──
      tmrOrderCreated();

      // ── Собственный трекер: конверсия order_created ──
      captureEvent('order_created', {
        order_id: data.order.id,
        items_count: orderPayload.items.length,
      }, { value: data.order.total, currency: 'RUB' });

      // Адрес — в книгу. Без await и без обработки на этом пути: заказ уже
      // создан, редирект на оплату ждать сохранение адреса не должен.
      void rememberDeliveryAddress();

      // 2. Подготовка к уходу на оплату. Заказ уже создан, деньги ещё не взяты —
      //    ни одна из этих операций не имеет права сорвать редирект.
      try {
        // safeSetItem: голый localStorage.setItem падает в Safari с «блокировать
        // все cookie» и уводил в catch уже созданный заказ.
        safeSetItem('lastOrderDetails', JSON.stringify({
          ...orderPayload,
          id: data.order.id,
          created_at: new Date().toISOString(),
        }));

        // Чистим только платёжное состояние и закрываем шторку. resetCheckout()
        // здесь стирал адрес/квартиру/подъезд/телефон ещё ДО оплаты — вернувшийся
        // после отказа банка заполнял форму заново. Полный сброс делает /success,
        // когда оплата подтверждена.
        const store = useCheckoutStore.getState();
        store.clearPaymentData();
        store.closeCheckout();
      } catch (prepErr) {
        captureEvent('payment_redirect_failed', {
          stage: 'storage',
          order_id: data.order.id,
          error: prepErr instanceof Error ? prepErr.message : String(prepErr),
        });
      }

      // 3. Redirect to payment ВСЕГДА через нашу /pay/redirect страницу —
      // она даёт точку контроля (payment_form_visited event) + fallback на
      // ручной клик если auto-submit заблокирован браузером.
      // Без этой страницы было: 85% клиентов попадают на PayKeeper и НЕ платят,
      // и мы не понимаем почему.
      redirecting = true;
      goToPayRedirect({
        orderId: data.order.id,
        sum: data.order.total,
        domain: data.paykeeperDomain,
        fallbackUrl: data.paymentUrl,
        phone: contact.phone,
      });

    } catch (error) {
      // Сюда долетают только серверные ошибки (400/500 с JSON-телом) или throw из
      // успешных веток (например, ошибки PayKeeper-формы). Сеть уже отработана выше.
      console.error('Order submission failed:', error);
      const message = error instanceof Error ? error.message : 'Ошибка при оформлении заказа';
      const rejection = error instanceof OrderRequestError ? error : null;

      // Серверный отказ раньше жил только в console.error: в воронке между
      // payment_started и order_created была пустота.
      captureEvent('order_rejected', {
        http_status: rejection?.status ?? 0,
        error_code: rejection?.code ?? null,
        field: rejection?.field ?? null,
        message,
        cart_value: cartTotal,
        point_id: selectedAddress?.deliveryPointId ?? null,
      });

      toast.error(message);

      // Возвращаем на шаг, где ошибку можно исправить. Поле field бэк присылает
      // только для адреса, для телефона распознаём по тексту ответа.
      if (rejection) {
        if (rejection.field === 'delivery.address' || /адрес/i.test(message)) {
          setErrors({ address: true });
          setShowValidationMessage(true);
          startTransition(() => setStep('delivery-info'));
        } else if (rejection.field === 'contact.phone' || /телефон/i.test(message)) {
          setErrors({ phone: true });
          setShowValidationMessage(true);
          startTransition(() => setStep('delivery-info'));
        }
      }
    } finally {
      // При уходе на оплату гард не снимаем — иначе повторный клик в момент
      // навигации создаст второй заказ.
      if (!redirecting) {
        inFlightRef.current = false;
        setIsSubmitting(false);
      }
    }
  }, [
    validateOrder,
    checkAvailability,
    cartItems,
    cartTotal,
    deliveryTime,
    deliveryDetails,
    effectiveDeliveryCost,
    minOrderSum,
    contact,
    preferences,
    total,
    effectiveCoins,
    selectedAddress,
    setErrors,
    setShowValidationMessage,
    setStep,
    rememberDeliveryAddress,
  ]);

  // Format delivery time text
  const estimatedTime = selectedAddress?.estimatedTime || '40-60';
  const deliveryTimeText = deliveryTime.type === 'now' 
    ? `Как можно скорее (${estimatedTime} минут)`
    : `${deliveryTime.selectedDate}, ${deliveryTime.selectedTime}`;
  
  // Format address — styled receipt view
  const addressParts: { label: string; value: string }[] = [];
  if (!deliveryDetails.isPrivateHouse) {
    if (deliveryDetails.apartment) addressParts.push({ label: 'Кв.', value: deliveryDetails.apartment });
    if (deliveryDetails.entrance) addressParts.push({ label: 'Подъезд', value: deliveryDetails.entrance });
    if (deliveryDetails.floor) addressParts.push({ label: 'Этаж', value: deliveryDetails.floor });
    if (deliveryDetails.intercom) addressParts.push({ label: 'Домофон', value: deliveryDetails.intercom });
  }
  
  const addressNode = (
    <div>
      <span className="block">{deliveryDetails.address}</span>
      {addressParts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {addressParts.map((part) => (
            <span
              key={part.label}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-xs font-medium',
                'bg-[var(--primary)]/10 text-[var(--primary)]'
              )}
            >
              {part.label} {part.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
  
  return (
    <div className="p-4">
      {/* Minimum Order Warning */}
      {minOrderEligible < minOrderSum && minOrderSum > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'p-3 rounded-card mb-4 border',
            isDark
              ? 'bg-[var(--error)]/10 border-[var(--error)]/20'
              : 'bg-[var(--error-bg)] border-[var(--border-error)]'
          )}
        >
          <div className="flex items-start gap-2">
            <Warning weight="fill" className="w-5 h-5 text-[var(--error)] flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--error)]">
                Сумма заказа ниже минимальной!
              </p>
              <p className="text-xs text-[var(--error)] mt-1">
                Добавьте товаров на {formattedRemaining}
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Header */}
      <div className={cn(
        'p-4 rounded-card mb-4',
        'bg-[var(--primary-subtle)]'
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-12 h-12 rounded-card flex items-center justify-center',
            isDark ? 'bg-[var(--primary)]/20' : 'bg-[var(--primary)]'
          )}>
            <Receipt weight="fill" className={cn(
              'w-6 h-6',
              isDark ? 'text-[var(--primary)]' : 'text-[var(--text-inverse)]'
            )} />
          </div>
          <div>
            <h3 className={cn(
              'font-semibold',
              'text-[var(--text-primary)]'
            )}>
              Проверьте заказ
            </h3>
            <p className={cn(
              'text-sm',
              isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'
            )}>
              Убедитесь, что всё верно
            </p>
          </div>
        </div>
      </div>
      
      {/* Cart Items */}
      <div className={cn(
        'rounded-card border p-4 mb-4',
        isDark
          ? 'bg-[var(--surface-secondary)] border-[var(--border-default)]'
          : 'bg-[var(--surface-primary)] border-[var(--border-default)]'
      )}>
        <div className="flex items-center gap-2 mb-3">
          <ShoppingCart weight="fill" className="w-5 h-5 text-[var(--primary)]" />
          <h4 className={cn(
            'font-semibold text-sm',
            'text-[var(--text-primary)]'
          )}>
            Товары в корзине
          </h4>
        </div>
        
        <div className={cn(
          'divide-y',
          isDark ? 'divide-[var(--border-default)]' : 'divide-gray-100'
        )}>
          {/* Ключ составной: платная строка и подарок могут иметь один id */}
          {cartItems.map((item) => (
            <OrderItemCard key={`${item.id}-${item.bonusType ?? 'regular'}`} item={item} isDark={isDark} />
          ))}
        </div>
        
        {/* Totals */}
        <div className={cn(
          'mt-3 pt-3 border-t',
          isDark ? 'border-[var(--border-default)]' : 'border-gray-100'
        )}>
          <div className="flex justify-between text-sm mb-1">
            <span className={isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'}>Товары:</span>
            <span className={cn('font-medium font-mono', 'text-[var(--text-primary)]')}>
              {formattedSubtotal}
            </span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className={isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'}>Доставка:</span>
            <span className={cn('font-medium font-mono', 'text-[var(--text-primary)]')}>
              {isFreeDeliveryPromo ? (
                <>
                  <span className="line-through text-[var(--text-tertiary)] mr-1.5">{formatPrice(deliveryCost)}</span>
                  <span className="text-[var(--success)]">Бесплатно</span>
                </>
              ) : (
                formattedDeliveryCost
              )}
            </span>
          </div>
          {effectiveCoins > 0 && (
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[var(--success)]">Коины:</span>
              <span className="font-medium font-mono text-[var(--success)]">
                −{formatPrice(effectiveCoins)}
              </span>
            </div>
          )}
          <div className={cn(
            'flex justify-between pt-2 border-t',
            isDark ? 'border-[var(--border-default)]' : 'border-gray-100'
          )}>
            <span className={cn('font-semibold', 'text-[var(--text-primary)]')}>
              Итого:
            </span>
            {/* Обе ветки давали один и тот же #7B42FF: --primary равен ему
                и в светлой, и в тёмной теме. Развилка была фиктивной —
                та же, что свёрнута в delivery-details-step.tsx. Цвет
                итоговой суммы побайтово прежний. */}
            <span className={cn('text-lg font-bold font-mono', 'text-[var(--primary)]')}>
              {formattedTotal}
            </span>
          </div>
        </div>
      </div>
      
      {/* Delivery Info */}
      <div className={cn(
        'rounded-card border p-4 mb-4',
        isDark
          ? 'bg-[var(--surface-secondary)] border-[var(--border-default)]'
          : 'bg-[var(--surface-primary)] border-[var(--border-default)]'
      )}>
        <div className="flex items-center gap-2 mb-2">
          <MapPin weight="fill" className="w-5 h-5 text-[var(--primary)]" />
          <h4 className={cn(
            'font-semibold text-sm',
            'text-[var(--text-primary)]'
          )}>
            Информация о доставке
          </h4>
        </div>
        
        <div className={cn(
          'divide-y',
          isDark ? 'divide-[var(--border-default)]' : 'divide-gray-100'
        )}>
          <InfoRow icon={User} label="Имя:" value={contact.name} isDark={isDark} />
          <InfoRow icon={Phone} label="Телефон:" value={contact.phone} isDark={isDark} />
          <InfoRow icon={MapPin} label="Адрес:" value={addressNode} isDark={isDark} />
          <InfoRow icon={Clock} label="Время доставки:" value={deliveryTimeText} isDark={isDark} />
          
          {deliveryDetails.comment && (
            <InfoRow icon={ChatText} label="Комментарий:" value={deliveryDetails.comment} isDark={isDark} />
          )}
        </div>
        
        {/* Preferences */}
        {(preferences.noGreens || preferences.needCall) && (
          <div className={cn(
            'mt-3 pt-3 border-t',
            isDark ? 'border-[var(--border-default)]' : 'border-gray-100'
          )}>
            <span className={cn(
              'text-xs',
              isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'
            )}>
              Дополнительные пожелания:
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {preferences.noGreens && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-pill bg-[var(--primary)]/10">
                  <Leaf weight="fill" className="w-4 h-4 text-[var(--primary)]" />
                  <span className="text-xs font-medium text-[var(--primary)]">Без зелени</span>
                </div>
              )}
              {preferences.needCall && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-pill bg-[var(--primary)]/10">
                  <PhoneCall weight="fill" className="w-4 h-4 text-[var(--primary)]" />
                  <span className="text-xs font-medium text-[var(--primary)]">Нужен звонок</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Loyalty Coins */}
      {isAuthenticated && loyaltyData && (
        <div className={cn(
          'rounded-card overflow-hidden mb-4',
          isDark ? 'border border-[var(--border-default)]' : 'border border-[var(--border-default)]'
        )}>
          {/* Баланс коинов — всегда видно */}
          <div className="bg-gradient-to-r from-[#7B42FF] to-[#9B6FFF] p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coin weight="fill" className="w-5 h-5" />
                <span className="font-semibold text-sm">Мои коины</span>
              </div>
              <div className="text-right">
                <span className="text-xl font-bold font-mono">{coinBalance}</span>
                <span className="text-xs text-white/70 ml-1">коинов</span>
              </div>
            </div>
          </div>

          {/* Состояние: можно списать */}
          {canSpendCoins && (
            <div className={cn('p-4', isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--surface-primary)]')}>
              <label className="flex items-center justify-between cursor-pointer">
                <span className={cn('text-sm font-medium', 'text-[var(--text-primary)]')}>
                  Списать коины
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={effectiveCoins > 0}
                  onClick={() => setLoyaltyCoins(effectiveCoins > 0 ? 0 : maxCoinsAllowed)}
                  className={cn(
                    'relative inline-flex h-6 w-11 items-center rounded-pill transition-colors',
                    effectiveCoins > 0 ? 'bg-[var(--primary)]' : (isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-gray-200')
                  )}
                >
                  <span className={cn(
                    'inline-block h-4 w-4 rounded-pill bg-white transition-transform',
                    effectiveCoins > 0 ? 'translate-x-6' : 'translate-x-1'
                  )} />
                </button>
              </label>

              {effectiveCoins > 0 && (
                <div className="mt-3">
                  <input
                    type="range"
                    min={0}
                    max={maxCoinsAllowed}
                    step={1}
                    value={effectiveCoins}
                    onChange={(e) => setLoyaltyCoins(Number(e.target.value))}
                    className="w-full accent-[var(--primary)]"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className={cn('text-xs', 'text-[var(--text-tertiary)]')}>0</span>
                    <span className="text-sm font-bold text-[var(--primary)]">
                      Скидка: {formatPrice(effectiveCoins)}
                    </span>
                    <span className={cn('text-xs', 'text-[var(--text-tertiary)]')}>{maxCoinsAllowed}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Состояние: баланс 0 */}
          {coinBalance === 0 && (
            <div className={cn('px-4 py-3', isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--surface-primary)]')}>
              <p className={cn('text-xs', 'text-[var(--text-secondary)]')}>
                Коины начисляются после доставки заказа — {loyaltyData.earn_rate}% от суммы
              </p>
            </div>
          )}

          {/* Состояние: есть коины, но заказ меньше минимального.
              Сравниваем с coinsBaseTotal (товары + доставка) — по той же
              формуле, по которой сервер решает, можно ли списывать. */}
          {coinBalance > 0 && coinsBaseTotal < minCartForSpend && (
            <div className={cn('px-4 py-3', isDark ? 'bg-[var(--surface-secondary)]' : 'bg-[var(--surface-primary)]')}>
              <p className={cn('text-xs', 'text-[var(--text-secondary)]')}>
                Списание доступно от {formatPrice(minCartForSpend)} — добавьте ещё {formatPrice(minCartForSpend - coinsBaseTotal)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Submit Button */}
      <motion.button
        onClick={handleSubmit}
        disabled={isSubmitting || (minOrderEligible < minOrderSum && minOrderSum > 0)}
        className={cn(
          'w-full py-4 rounded-card font-semibold text-base flex items-center justify-center gap-2',
          'text-[var(--text-inverse)]',
          'disabled:opacity-70 disabled:cursor-not-allowed',
          'transition-all'
        )}
        // Фиолетовый ореол под «Оплатить» — единственное МЕСТО В КОРЗИНЕ, где
        // он реально рисуется (инлайном, поэтому мимо мёртвых shadow-классов).
        // Ответ 35 = Б: свечение сохраняется. На shadow-glow не переводим —
        // роль даёт 0 0 20px/0.10 без смещения и ослабила бы ореол вдвое
        // именно на кнопке оплаты. Не трогать, не «уплощать».
        style={{
          background: 'var(--primary)',
          boxShadow: '0 8px 24px rgba(var(--primary-rgb), 0.25)'
        }}
        whileTap={!isSubmitting ? { scale: 0.98 } : {}}
      >
        {isSubmitting ? (
          <>
            <Spinner weight="bold" className="w-5 h-5 animate-spin" />
            Оформляем заказ...
          </>
        ) : (
          <>
            <CreditCard weight="fill" className="w-5 h-5" />
            Оплатить {formatPrice(total)}
          </>
        )}
      </motion.button>

      {unavailable && (
        <UnavailableItemsDialog
          open
          items={unavailable}
          cartIsEmpty={cartItems.length === 0}
          belowMinimum={minOrderSum > 0 && minOrderEligible < minOrderSum}
          onContinue={() => {
            setUnavailable(null);
            handleSubmit();
          }}
          onBackToCart={() => {
            setUnavailable(null);
            startTransition(() => {
              setStep('cart');
            });
          }}
        />
      )}
    </div>
  );
}

